import { normalizeServerSchema } from '@sindi/product-engine/core';
import { getApiUrl } from '../libs/api';
import { getAuthToken } from '../libs/auth';

const SCHEMA_ENDPOINT = '/api/product-schemas';
const CATALOG_ENDPOINT = `${SCHEMA_ENDPOINT}/catalog`;

export const isServerSchemasEnabled = () => {
  const flag = `${process.env.REACT_APP_USE_SERVER_SCHEMAS || ''}`
    .trim()
    .toLowerCase();
  return flag === 'on' || flag === 'true';
};

const buildUrl = (baseUrl, endpoint) =>
  `${String(baseUrl || getApiUrl() || '').replace(/\/+$/, '')}${endpoint}`;

const buildSchemaUrl = (baseUrl, productType) =>
  buildUrl(baseUrl, `${SCHEMA_ENDPOINT}/${encodeURIComponent(productType)}`);

const buildRequestOptions = (requestOptions = {}) => {
  const token = getAuthToken();
  return {
    ...requestOptions,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(requestOptions.headers || {}),
    },
  };
};

export const loadCatalog = async (options = {}) => {
  const fetchImpl =
    options.fetchImpl || (typeof fetch === 'function' ? fetch : null);

  if (!fetchImpl) {
    throw new Error('Catalog loading requires a fetch implementation.');
  }

  const response = await fetchImpl(
    buildUrl(options.baseUrl, CATALOG_ENDPOINT),
    buildRequestOptions(options.requestOptions)
  );

  if (!response?.ok) {
    throw new Error('Unable to load product schema catalog.');
  }

  const payload = await response.json();
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

export const loadSchema = async (productType, options = {}) => {
  const fetchImpl =
    options.fetchImpl || (typeof fetch === 'function' ? fetch : null);

  if (!fetchImpl) {
    throw new Error('Schema loading requires a fetch implementation.');
  }

  if (!productType) {
    throw new Error('Product type is required to load a schema.');
  }

  const response = await fetchImpl(
    buildSchemaUrl(options.baseUrl, productType),
    buildRequestOptions(options.requestOptions)
  );

  if (!response?.ok) {
    throw new Error(`Unable to load product schema for "${productType}".`);
  }

  const payload = await response.json();
  const root = payload?.data ?? payload;
  return normalizeServerSchema(root);
};
